import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/layout/AppShell";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  RefreshCw, Mail, CheckCircle2, AlertTriangle, XCircle, Clock, Eye,
  MousePointerClick, Archive, ArchiveRestore, Trash2, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  sent:        { label: "Sent",        cls: "bg-blue-50 text-blue-700 border-blue-200",        icon: Mail },
  delivered:   { label: "Delivered",   cls: "bg-green-50 text-green-700 border-green-200",     icon: CheckCircle2 },
  opened:      { label: "Opened",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Eye },
  clicked:     { label: "Clicked",     cls: "bg-indigo-50 text-indigo-700 border-indigo-200",   icon: MousePointerClick },
  delayed:     { label: "Delayed",     cls: "bg-amber-50 text-amber-700 border-amber-200",      icon: Clock },
  bounced:     { label: "Bounced",     cls: "bg-red-50 text-red-700 border-red-200",            icon: XCircle },
  complained:  { label: "Marked Spam", cls: "bg-red-50 text-red-700 border-red-200",            icon: AlertTriangle },
  failed:      { label: "Failed",      cls: "bg-red-50 text-red-700 border-red-200",            icon: XCircle },
};

// Minimum retention enforced by the server; mirrored here only for a friendlier message.
const MIN_DELETE_AGE_DAYS = 30;

const EmailActivityPage = ({ embedded = false }) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [counts, setCounts] = useState({ activeCount: 0, archivedCount: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [view, setView] = useState("active");
  const [confirmDelete, setConfirmDelete] = useState(null); // a row, or {bulk:'90d'|'6m'|'1y'}

  const fetchLogs = async (v = view, statusF = statusFilter) => {
    setLoading(true);
    try {
      const params = { params: { view: v, ...(statusF ? { status: statusF } : {}) } };
      const resp = await axios.get(`${API}/email-activity`, params);
      setLogs(resp.data?.logs || []);
      setSummary(resp.data?.summary || {});
      setCounts({
        activeCount: resp.data?.activeCount || 0,
        archivedCount: resp.data?.archivedCount || 0,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLogs(view, statusFilter);
  }, [view, statusFilter]);

  const changeView = (v) => { setStatusFilter(""); setView(v); };

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      const res = await fn();
      if (successMsg) toast.success(typeof successMsg === "function" ? successMsg(res) : successMsg);
      await fetchLogs(view, statusFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
    setBusy(false);
  };

  const archiveOne  = (id) => runAction(() => axios.post(`${API}/email-activity/${id}/archive`), "Archived");
  const restoreOne  = (id) => runAction(() => axios.post(`${API}/email-activity/${id}/restore`), "Restored");
  const archiveAll  = () => runAction(() => axios.post(`${API}/email-activity/archive-all`),
                                      (r) => `Archived ${r.data?.archived ?? 0} email(s)`);
  const archiveOlder = (days) => runAction(
    () => axios.post(`${API}/email-activity/archive-older-than`, null, { params: { days } }),
    (r) => `Archived ${r.data?.archived ?? 0} email(s) older than ${days} days`);
  const deleteOne = (id) => runAction(() => axios.delete(`${API}/email-activity/${id}`), "Permanently deleted");
  const clearArchive = (period) => runAction(
    () => axios.delete(`${API}/email-activity/clear-archive`, { params: { period } }),
    (r) => `Deleted ${r.data?.deleted ?? 0} archived email(s)`);

  const renderBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || { label: status || "?", cls: "bg-gray-50 text-gray-700 border-gray-200", icon: Mail };
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.cls}`}>
        <Icon className="w-3 h-3" />{cfg.label}
      </span>
    );
  };

  const isArchived = view === "archived";

  const body = (
    <div className={embedded ? "p-4 space-y-4" : "max-w-6xl mx-auto px-4 py-6 space-y-4"}>
      {!embedded && (
        <p className="text-sm text-ink-muted">
          Every email this app has sent and its delivery status from Resend.
        </p>
      )}

      {/* Active / Archived tabs */}
      <Tabs value={view} onValueChange={changeView}>
        <TabsList data-testid="email-activity-tabs">
          <TabsTrigger value="active" data-testid="tab-active">Active ({counts.activeCount})</TabsTrigger>
          <TabsTrigger value="archived" data-testid="tab-archived">Archived ({counts.archivedCount})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Bulk controls */}
      <div className="flex flex-wrap items-center gap-2">
        {!isArchived ? (
          <>
            <Button variant="outline" size="sm" disabled={busy || counts.activeCount === 0}
                    onClick={archiveAll} data-testid="archive-all-btn">
              <Archive className="w-4 h-4 mr-1" /> Archive All
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={busy} data-testid="archive-older-btn">
                  <Archive className="w-4 h-4 mr-1" /> Archive Older Than <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {[30, 60, 90].map((d) => (
                  <DropdownMenuItem key={d} onClick={() => archiveOlder(d)} data-testid={`archive-older-${d}`}>
                    {d} days
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={busy || counts.archivedCount === 0}
                      className="text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-archive-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Clear Archive Older Than <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {[["90d", "90 days"], ["6m", "6 months"], ["1y", "1 year"]].map(([p, label]) => (
                <DropdownMenuItem key={p} onClick={() => setConfirmDelete({ bulk: p, label })}
                                  data-testid={`clear-archive-${p}`}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button variant="outline" size="sm" onClick={() => fetchLogs(view, statusFilter)}
                disabled={loading} className="ml-auto" data-testid="refresh-email-activity">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Status filter pills (within current view) */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setStatusFilter("")} data-testid="filter-all"
          className={`px-3 py-1.5 text-xs rounded-full font-medium border transition ${
            statusFilter === "" ? "bg-brand-primary text-white border-brand-primary"
                                : "bg-white text-ink-main border-gray-300 hover:bg-gray-50"}`}>
          All ({Object.values(summary).reduce((a, b) => a + b, 0)})
        </button>
        {Object.entries(summary).map(([s, count]) => {
          const cfg = STATUS_CONFIG[s] || { label: s };
          return (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
              className={`px-3 py-1.5 text-xs rounded-full font-medium border transition ${
                statusFilter === s ? "bg-brand-primary text-white border-brand-primary"
                                   : "bg-white text-ink-main border-gray-300 hover:bg-gray-50"}`}>
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isArchived ? "Archived emails" : "Recent emails"}</CardTitle>
          <CardDescription>
            {isArchived
              ? "Archived emails still receive delivery updates. Permanent delete is allowed only after 30 days."
              : "Most recent first. Statuses update automatically as Resend sends events back."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center text-sm text-ink-muted py-10">
              No {isArchived ? "archived " : ""}emails {statusFilter ? `with status "${statusFilter}"` : ""} found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-muted border-b">
                    <th className="px-3 py-2">Sent</th>
                    <th className="px-3 py-2">To</th>
                    <th className="px-3 py-2">Subject</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Last event</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((row) => (
                    <tr key={row.id} data-testid={`email-log-${row.id}`} className="border-b hover:bg-blue-50/40">
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-muted">
                        {row.sentAt ? new Date(row.sentAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-3 py-2 text-ink-main">{row.toEmail || "—"}</td>
                      <td className="px-3 py-2 text-ink-main">{row.subject || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">{row.emailType || "—"}</span>
                      </td>
                      <td className="px-3 py-2">{renderBadge(row.status)}</td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {row.lastEventAt ? new Date(row.lastEventAt).toLocaleString() : "—"}
                        {row.error ? <div className="text-red-600 mt-0.5">{row.error}</div> : null}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {!isArchived ? (
                          <Button variant="ghost" size="sm" disabled={busy}
                                  onClick={() => archiveOne(row.id)} data-testid={`archive-${row.id}`}>
                            <Archive className="w-4 h-4 mr-1" /> Archive
                          </Button>
                        ) : (
                          <div className="inline-flex gap-1">
                            <Button variant="ghost" size="sm" disabled={busy}
                                    onClick={() => restoreOne(row.id)} data-testid={`restore-${row.id}`}>
                              <ArchiveRestore className="w-4 h-4 mr-1" /> Restore
                            </Button>
                            <Button variant="ghost" size="sm" disabled={busy}
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => setConfirmDelete(row)} data-testid={`delete-${row.id}`}>
                              <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm permanent delete (single row OR bulk clear) */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.bulk
                ? `This permanently deletes archived emails older than ${confirmDelete.label}. This cannot be undone. Records newer than ${MIN_DELETE_AGE_DAYS} days are always kept.`
                : "This permanently deletes this archived Email Activity record. This cannot be undone. It will not change the related invoice, payment, or the invoice’s EMAIL BOUNCED flag."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm"
              onClick={() => {
                const target = confirmDelete;
                setConfirmDelete(null);
                if (target?.bulk) clearArchive(target.bulk);
                else if (target?.id) deleteOne(target.id);
              }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (embedded) return body;
  return <AppShell pageTitle="Email Activity" breadcrumb>{body}</AppShell>;
};

export default EmailActivityPage;
